/**
 * BreakGlassPage
 *
 * Admin page to manage break glass accounts for this org.
 * Provisioned break glass accounts are off-domain tenant identities with an
 * org-admin profile. Revocation disables the identity and its active sessions.
 *
 * Route: /settings/identity/break-glass  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { Trash2, Plus, ShieldAlert } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import {
  getOrgSettings,
  provisionBreakGlassAccount,
  revokeBreakGlassAccount,
} from "../../api/adminUsers";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes";

const BreakGlassPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [revokingEmail, setRevokingEmail] = useState("");
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState("");

  useEffect(() => {
    if (orgAvailable === false) navigate(ROUTES.SETTINGS.MODELS, { replace: true });
  }, [orgAvailable, navigate]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrgSettings();
      setEmails(data.break_glass_emails ?? []);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load break glass accounts.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleAdd = async () => {
    setAddError("");
    const trimmed = newEmail.trim().toLowerCase();
    const trimmedName = newName.trim();
    if (!trimmed) { setAddError("Email is required."); return; }
    if (!trimmed.includes("@") || !trimmed.includes(".")) { setAddError("Enter a valid email address."); return; }
    if (!trimmedName) { setAddError("Name is required."); return; }
    if (emails.includes(trimmed)) { setAddError("This email is already in the list."); return; }
    setProvisioning(true);
    try {
      const result = await provisionBreakGlassAccount(trimmed, trimmedName);
      setEmails((prev) => [...prev, result.email]);
      setNewEmail("");
      setNewName("");
      showFeedback(
        result.email_sent === false
          ? "Account provisioned, but the setup email could not be sent."
          : "Break glass account provisioned and setup email sent.",
        result.email_sent === false ? "warning" : "success"
      );
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to provision break glass account.", "error");
    } finally {
      setProvisioning(false);
    }
  };

  const handleRemove = async (email) => {
    setRevokingEmail(email);
    try {
      await revokeBreakGlassAccount(email);
      setEmails((prev) => prev.filter((existing) => existing !== email));
      showFeedback("Break glass account revoked.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to revoke break glass account.", "error");
    } finally {
      setRevokingEmail("");
    }
  };

  return (
    <SettingsPageShell
      title="Break Glass Accounts"
      description="Off-domain email addresses that can log in as org admins. Use only for operations personnel who don't have a domain email."
    >
      {loading ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-6 max-w-2xl">

          {/* Warning banner */}
          <div className="flex items-start gap-3 p-3.5 rounded-xl bg-fill-hex-fef3c7 dark:bg-fill-hex-d97706/10 border border-edge-amber-200 dark:border-edge-hex-d97706/40">
            <ShieldAlert className="w-4 h-4 text-ink-hex-d97706 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-ink-amber-800 dark:text-ink-amber-300 leading-relaxed">
              Break glass accounts bypass domain restrictions and receive org-admin access.
              Provisioning sends a password setup link; revocation disables access immediately.
            </p>
          </div>

          {/* Current list */}
          {emails.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {emails.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-surface-muted-deep dark:bg-surface-muted-deep border border-edge-gray-200 dark:border-edge-dialog-border"
                >
                  <span className="text-sm font-mono text-ink-ink dark:text-ink-gray-200">{email}</span>
                  <button
                    onClick={() => handleRemove(email)}
                    disabled={revokingEmail === email}
                    className="p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-red-500 hover:bg-fill-red-50 dark:hover:bg-fill-red-500/10 transition-colors"
                    title={`Revoke ${email}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-gray-400 dark:text-ink-gray-500">No break glass accounts configured.</p>
          )}

          {/* Add new */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300">Provision account</p>
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <Input
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setAddError(""); }}
                placeholder="Operations admin"
              />
              <div>
                <Input
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setAddError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  placeholder="ops@example.com"
                  type="email"
                />
              </div>
              <button
                onClick={handleAdd}
                disabled={provisioning}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-fill-gray-100 dark:bg-fill-dialog-surface text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-200 dark:hover:bg-fill-dialog-border border border-edge-gray-200 dark:border-edge-dialog-border transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> {provisioning ? "Provisioning..." : "Provision"}
              </button>
            </div>
            {addError && <p className="text-xs text-ink-red-500">{addError}</p>}
          </div>

        </div>
      )}
    </SettingsPageShell>
  );
};

export default BreakGlassPage;
